import { useRef } from "react";
import type { DragonDossier } from "../types";
import { DragonArt } from "./DragonArt";
import { EditableText } from "./Editable";
import { Icon } from "./Icon";
import { gsap, useGSAP } from "../lib/animation";

interface HeroProps {
  dossier: DragonDossier;
  onEdit: (mutator: (draft: DragonDossier) => void) => void;
}

export function Hero({ dossier, onEdit }: HeroProps) {
  const { dragon, appearance, rules } = dossier;
  const heroRef = useRef<HTMLElement | null>(null);

  useGSAP(
    () => {
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (reduceMotion) return;

      const timeline = gsap.timeline({
        defaults: { duration: 0.72, ease: "power3.out" }
      });

      timeline
        .from(".dragon-atmosphere", {
          autoAlpha: 0,
          scale: 0.96,
          duration: 0.58
        })
        .from(".dragon-art__image, .dragon-art__silhouette", {
          autoAlpha: 0,
          scale: 1.04,
          x: 24,
          stagger: 0.08
        })
        .from(
          ".dragon-title",
          {
            y: 40,
            autoAlpha: 0,
            filter: "blur(18px)",
            duration: 0.95
          },
          "<0.04"
        )
        .from(
          ".dragon-subtitle, .dragon-lineage, .dragon-summary, .dragon-dna span",
          {
            y: 18,
            autoAlpha: 0,
            stagger: 0.06
          },
          "<0.34"
        )
        .from(
          ".tag-input",
          {
            y: 8,
            scale: 0.92,
            stagger: 0.035,
            duration: 0.42
          },
          "<0.18"
        )
        .from(
          ".hero-signal-strip span",
          {
            x: -12,
            stagger: 0.045,
            duration: 0.42
          },
          "<0.08"
        )
        .from(
          ".hero-stats > div, .summary-card",
          {
            y: 14,
            stagger: { amount: 0.34, from: "start" },
            duration: 0.52
          },
          "<0.12"
        )
        .from(
          ".hero-rules-note",
          {
            x: 10,
            duration: 0.45
          },
          "<0.2"
        );

      const root = heroRef.current;
      if (!root) return;

      const targets = [
        { node: root.querySelector(".dragon-art__image"), x: -14, y: -8 },
        { node: root.querySelector(".dragon-art__silhouette"), x: 10, y: 6 },
        { node: root.querySelector(".dragon-atmosphere"), x: 20, y: 12 }
      ].filter((target): target is { node: Element; x: number; y: number } => Boolean(target.node));

      const channels = targets.map((target) => ({
        xTo: gsap.quickTo(target.node, "x", { duration: 0.65, ease: "power3.out" }),
        yTo: gsap.quickTo(target.node, "y", { duration: 0.65, ease: "power3.out" }),
        x: target.x,
        y: target.y
      }));

      const move = (event: PointerEvent) => {
        const bounds = root.getBoundingClientRect();
        const x = (event.clientX - bounds.left) / bounds.width - 0.5;
        const y = (event.clientY - bounds.top) / bounds.height - 0.5;
        channels.forEach((channel) => {
          channel.xTo(x * channel.x);
          channel.yTo(y * channel.y);
        });
      };

      const leave = () => {
        channels.forEach((channel) => {
          channel.xTo(0);
          channel.yTo(0);
        });
      };

      root.addEventListener("pointermove", move);
      root.addEventListener("pointerleave", leave);
      return () => {
        root.removeEventListener("pointermove", move);
        root.removeEventListener("pointerleave", leave);
      };
    },
    {
      scope: heroRef,
      dependencies: [dragon.lineage, dragon.ageClass, appearance.portrait],
      revertOnUpdate: true
    }
  );

  const hoardValue = dossier.hoard.totalValue.toLocaleString("en-US");
  const minionCount =
    dossier.factions.filter((faction) => faction.relation === "serves").length || "Many";
  const topEffect = dossier.regionalEffects[0]?.name ?? "Latent";

  return (
    <section
      className="dragon-hero ornate-panel"
      ref={heroRef}
      aria-label={`${dragon.name}, ${dragon.ageClass} ${dragon.lineageLabel}`}
    >
      <DragonArt
        lineage={dragon.lineage}
        portrait={appearance.portrait}
        alt={dossier.assets.portraitAlt}
        signature={`${dragon.name}:${appearance.horns}:${appearance.wings}:${appearance.signatureFeature}`}
      />
      <div className="ornate-frame" aria-hidden="true">
        <i /><i /><i /><i />
      </div>
      <div className="dragon-hero__content">
        <EditableText
          label="Dragon name"
          className="dragon-title"
          value={dragon.name}
          onChange={(value) => onEdit((draft) => void (draft.dragon.name = value))}
        />
        <EditableText
          label="Dragon title"
          className="dragon-subtitle"
          multiline
          rows={2}
          value={dragon.title}
          onChange={(value) => onEdit((draft) => void (draft.dragon.title = value))}
        />
        <div className="dragon-lineage">
          {dragon.ageClass} {dragon.lineageLabel}
        </div>
        <div className="hero-signal-strip" aria-label="Dragon profile at a glance">
          <span>{appearance.element}</span>
          <span>{dragon.alignment}</span>
          <span>{dragon.role}</span>
          <span>{appearance.eyes ?? appearance.markings}</span>
        </div>
        <div className="tag-row" aria-label="Temperament">
          {dragon.temperament.map((tag, index) => (
            <input
              key={`${tag}-${index}`}
              aria-label={`Temperament ${index + 1}`}
              className="tag-input"
              value={tag}
              onChange={(event) =>
                onEdit((draft) => {
                  draft.dragon.temperament[index] = event.target.value;
                })
              }
            />
          ))}
        </div>
        <div className="dragon-dna" aria-label="Distinctive anatomy">
          <span>{appearance.horns ?? appearance.silhouette}</span>
          <span>{appearance.wings ?? appearance.markings}</span>
          <span>{appearance.signatureFeature ?? appearance.battleScars ?? appearance.voice}</span>
        </div>
        <EditableText
          label="Dragon summary"
          className="dragon-summary"
          multiline
          rows={3}
          value={dragon.summary}
          onChange={(value) => onEdit((draft) => void (draft.dragon.summary = value))}
        />
        <div className="hero-stats" aria-label="Core statistics">
          <div>
            <Icon name="threat" className="hero-stat__icon" aria-hidden="true" />
            <span>Threat</span>
            <strong>{dragon.threat}</strong>
          </div>
          <div>
            <Icon name="challenge" className="hero-stat__icon" aria-hidden="true" />
            <span>Challenge</span>
            <strong>{dragon.challenge ?? "Neutral"}</strong>
          </div>
          <div>
            <Icon name="age" className="hero-stat__icon" aria-hidden="true" />
            <span>Age</span>
            <strong>{dragon.ageYears} years</strong>
          </div>
          <div>
            <Icon name="domain" className="hero-stat__icon" aria-hidden="true" />
            <span>Domain</span>
            <strong>{dragon.domain}</strong>
          </div>
        </div>
      </div>
      <div className="hero-cards" aria-label="Dossier highlights">
        <button
          type="button"
          className="summary-card"
          aria-label={`Lair: ${dossier.lair.name}, ${dossier.lair.type}`}
        >
          <Icon name="lair" className="summary-card__icon" aria-hidden="true" />
          <span>Lair</span>
          <strong>{dossier.lair.name}</strong>
          <small>{dossier.lair.type}</small>
        </button>
        <button
          type="button"
          className="summary-card"
          aria-label={`Hoard: ${hoardValue} gold pieces, ${dossier.hoard.distribution.visible}% visible`}
        >
          <Icon name="hoard" className="summary-card__icon" aria-hidden="true" />
          <span>Hoard</span>
          <strong>{hoardValue} gp</strong>
          <small>{dossier.hoard.distribution.visible}% visible</small>
        </button>
        <button
          type="button"
          className="summary-card"
          aria-label={`Followers: ${dossier.factions.length} factions, ${minionCount} minions`}
        >
          <Icon name="faction" className="summary-card__icon" aria-hidden="true" />
          <span>Followers</span>
          <strong>{dossier.factions.length} factions</strong>
          <small>{minionCount} minions</small>
        </button>
        <button
          type="button"
          className="summary-card"
          aria-label={`Regional effect: ${topEffect}`}
        >
          <Icon name="spark" className="summary-card__icon" aria-hidden="true" />
          <span>Regional effects</span>
          <strong>{topEffect}</strong>
          <small>{appearance.element}</small>
        </button>
      </div>
      <div className="hero-rules-note" title={rules.notice}>
        {rules.adapter.toUpperCase()} · AC {rules.armorClass} · HP {rules.hitPoints}
      </div>
    </section>
  );
}
